import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { FileSpreadsheet, Plus, Trash2, Edit, Download, Upload, Users, Calculator, TrendingUp, Eye, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ExcelUploader from './ExcelUploader';
import SpreadsheetViewer from './SpreadsheetViewer';
import { ExcelExporter } from './ExcelExporter';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ExcelGradeManagerProps {
  classId: number;
  className: string;
}

interface GradeSheet {
  id: number;
  class_id: number;
  teacher_id: number;
  sheet_name: string;
  original_filename: string;
  sheet_data: any[][];
  column_definitions: any[];
  has_unsaved_changes: boolean;
  last_modified_at: string;
  created_at: string;
}

interface GradeAnalytics {
  totalStudents: number;
  totalAssignments: number;
  averageGrade: number;
  passRate: number;
  formulaCells: number;
}

const ExcelGradeManager: React.FC<ExcelGradeManagerProps> = ({ classId, className }) => {
  const [gradeSheets, setGradeSheets] = useState<GradeSheet[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<GradeSheet | null>(null);
  const [currentSheetData, setCurrentSheetData] = useState<any[][]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [analytics, setAnalytics] = useState<GradeAnalytics | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();

  useEffect(() => {
    loadGradeSheets();
  }, [classId]);

  useEffect(() => {
    if (selectedSheet) {
      calculateAnalytics(selectedSheet.sheet_data);
    }
  }, [selectedSheet]);

  const loadGradeSheets = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await window.ezsite.apis.tablePage(16078, {
        PageNo: 1,
        PageSize: 100,
        Filters: [
        { name: 'class_id', op: 'Equal', value: classId }],

        OrderByField: 'created_at',
        IsAsc: false
      });

      if (error) throw error;

      const sheets = data.List.map((item: any) => ({
        ...item,
        sheet_data: JSON.parse(item.sheet_data || '[]'),
        column_definitions: JSON.parse(item.column_definitions || '[]')
      }));

      setGradeSheets(sheets);
      console.log('Loaded grade sheets:', sheets);
    } catch (error) {
      console.error('Error loading grade sheets:', error);
      toast({
        title: 'Error',
        description: 'Failed to load grade sheets',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAnalytics = (data: any[][]) => {
    if (!data || data.length <= 1) {
      setAnalytics(null);
      return;
    }

    const headers = data[0] || [];
    const dataRows = data.slice(1);

    const totalStudents = dataRows.length;
    const assignmentColumns = headers.slice(2);
    const totalAssignments = assignmentColumns.length;

    let totalGrades = 0;
    let gradeCount = 0;
    let passCount = 0;
    let formulaCells = 0;

    dataRows.forEach((row) => {
      row.slice(2).forEach((cell) => {
        if (cell?.isFormula) {
          formulaCells++;
        }

        const value = parseFloat(cell?.value);
        if (!isNaN(value)) {
          totalGrades += value;
          gradeCount++;
          if (value >= 70) passCount++;
        }
      });
    });

    const averageGrade = gradeCount > 0 ? totalGrades / gradeCount : 0;
    const passRate = gradeCount > 0 ? passCount / gradeCount * 100 : 0;

    setAnalytics({
      totalStudents,
      totalAssignments,
      averageGrade,
      passRate,
      formulaCells
    });
  };

  const handleFileUploaded = async (excelData: any, filename: string) => {
    try {
      setIsLoading(true);

      const { data: userInfo, error: userError } = await window.ezsite.apis.getUserInfo();
      if (userError) throw userError;

      const newSheet = {
        class_id: classId,
        teacher_id: userInfo.ID,
        sheet_name: excelData.sheetName || 'Grade Sheet',
        original_filename: filename,
        sheet_data: JSON.stringify(excelData.data),
        column_definitions: JSON.stringify(excelData.columnDefinitions),
        has_unsaved_changes: false,
        last_modified_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      };

      const { error } = await window.ezsite.apis.tableCreate(16078, newSheet);
      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Grade sheet uploaded successfully!',
        variant: 'default'
      });

      setShowUploadDialog(false);
      loadGradeSheets();
      setActiveTab('sheets');
    } catch (error) {
      console.error('Error uploading grade sheet:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload grade sheet',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSheetSelect = (sheet: GradeSheet) => {
    if (hasUnsavedChanges) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes. Do you want to discard them and switch sheets?'
      );
      if (!confirmDiscard) return;
    }

    setSelectedSheet(sheet);
    setCurrentSheetData(sheet.sheet_data);
    setHasUnsavedChanges(false);
    setActiveTab('editor');
  };

  const handleDataChange = (newData: any[][]) => {
    setCurrentSheetData(newData);
    setHasUnsavedChanges(true);
    calculateAnalytics(newData);
  };

  const handleSave = async () => {
    if (!selectedSheet) return;

    try {
      setIsLoading(true);

      const updatedSheet = {
        ...selectedSheet,
        sheet_data: JSON.stringify(currentSheetData),
        has_unsaved_changes: false,
        last_modified_at: new Date().toISOString()
      };

      const { error } = await window.ezsite.apis.tableUpdate(16078, updatedSheet);
      if (error) throw error;

      setSelectedSheet(updatedSheet);
      setHasUnsavedChanges(false);

      toast({
        title: 'Success',
        description: 'Grade sheet saved successfully!'
      });

      loadGradeSheets();
    } catch (error) {
      console.error('Error saving grade sheet:', error);
      toast({
        title: 'Error',
        description: 'Failed to save grade sheet',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (!selectedSheet || !currentSheetData) return;

    try {
      const filename = `${selectedSheet.original_filename.replace(/\.[^/.]+$/, '')}_modified.xlsx`;
      ExcelExporter.exportToExcel(currentSheetData, filename);

      toast({
        title: 'Success',
        description: 'Excel file exported successfully!'
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast({
        title: 'Error',
        description: 'Failed to export Excel file',
        variant: 'destructive'
      });
    }
  };

  const handleRevert = () => {
    if (selectedSheet) {
      setCurrentSheetData(selectedSheet.sheet_data);
      setHasUnsavedChanges(false);
      calculateAnalytics(selectedSheet.sheet_data);
      toast({
        title: 'Reverted',
        description: 'Changes have been reverted to the last saved version'
      });
    }
  };

  const handleDeleteSheet = async (sheetId: number) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this grade sheet? This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      const { error } = await window.ezsite.apis.tableDelete(16078, { ID: sheetId });
      if (error) throw error;

      if (selectedSheet?.id === sheetId) {
        setSelectedSheet(null);
        setCurrentSheetData([]);
        setHasUnsavedChanges(false);
        setAnalytics(null);
        setActiveTab('overview');
      }

      toast({
        title: 'Success',
        description: 'Grade sheet deleted successfully'
      });

      loadGradeSheets();
    } catch (error) {
      console.error('Error deleting grade sheet:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete grade sheet',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = () => {
    ExcelExporter.exportGradeSheetTemplate();
    toast({
      title: 'Template Downloaded',
      description: 'Professional grade sheet template has been downloaded'
    });
  };

  return (
    <div className="space-y-6" data-id="sg8rugvwe" data-path="src/components/grades/ExcelGradeManager.tsx">
      {/* Main Header */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" data-id="22415sxv4" data-path="src/components/grades/ExcelGradeManager.tsx">
        <CardHeader data-id="jy5nlvp33" data-path="src/components/grades/ExcelGradeManager.tsx">
          <div className="flex items-center justify-between" data-id="07taiutc1" data-path="src/components/grades/ExcelGradeManager.tsx">
            <div data-id="962x1rwqe" data-path="src/components/grades/ExcelGradeManager.tsx">
              <CardTitle className="flex items-center gap-3 text-2xl" data-id="ebhoc4uag" data-path="src/components/grades/ExcelGradeManager.tsx">
                <div className="p-2 bg-blue-100 rounded-lg" data-id="9jaao6aou" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <FileSpreadsheet className="h-6 w-6 text-blue-600" data-id="d7yc7j10f" data-path="src/components/grades/ExcelGradeManager.tsx" />
                </div>
                Professional Grade Management
                <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300" data-id="as8u68dy1" data-path="src/components/grades/ExcelGradeManager.tsx">
                  {className}
                </Badge>
              </CardTitle>
              <CardDescription className="text-lg mt-2" data-id="81kji9t5v" data-path="src/components/grades/ExcelGradeManager.tsx">
                Advanced Excel-like interface for managing student grades with professional features
              </CardDescription>
            </div>
            
            <div className="flex gap-3" data-id="1gqszxoas" data-path="src/components/grades/ExcelGradeManager.tsx">
              <Button variant="outline" onClick={downloadTemplate} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-id="b5ddzrrfp" data-path="src/components/grades/ExcelGradeManager.tsx">
                <Download className="h-4 w-4 mr-2" data-id="665m661eo" data-path="src/components/grades/ExcelGradeManager.tsx" />
                Download Template
              </Button>
              
              <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog} data-id="y126y7oiq" data-path="src/components/grades/ExcelGradeManager.tsx">
                <DialogTrigger asChild data-id="q6drdr5be" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-id="8v7i353f6" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <Upload className="h-4 w-4 mr-2" data-id="pdbvh8c0a" data-path="src/components/grades/ExcelGradeManager.tsx" />
                    Upload Grade Sheet
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" data-id="wrohg5nbu" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <DialogHeader data-id="o2prami5w" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <DialogTitle className="flex items-center gap-2" data-id="t6woga8ze" data-path="src/components/grades/ExcelGradeManager.tsx">
                      <Upload className="h-5 w-5" data-id="mleh4jfmp" data-path="src/components/grades/ExcelGradeManager.tsx" />
                      Upload Excel Grade Sheet
                    </DialogTitle>
                    <DialogDescription data-id="n8objtn6p" data-path="src/components/grades/ExcelGradeManager.tsx">
                      Upload an Excel file containing student grades and scores. The system will automatically detect formulas and preserve formatting.
                    </DialogDescription>
                  </DialogHeader>
                  <ExcelUploader
                    onFileUploaded={handleFileUploaded}
                    isLoading={isLoading} data-id="bt1rrut6j" data-path="src/components/grades/ExcelGradeManager.tsx" />

                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Quick Stats */}
      {analytics &&
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="q6r1wmoiz" data-path="src/components/grades/ExcelGradeManager.tsx">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="4gcwlckis" data-path="src/components/grades/ExcelGradeManager.tsx">
            <CardContent className="p-4" data-id="cvge8svl6" data-path="src/components/grades/ExcelGradeManager.tsx">
              <div className="flex items-center gap-3" data-id="5zfhl4owk" data-path="src/components/grades/ExcelGradeManager.tsx">
                <Users className="h-8 w-8 text-blue-600" data-id="c6qrr6im4" data-path="src/components/grades/ExcelGradeManager.tsx" />
                <div data-id="nz0l9gxkx" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <p className="text-sm font-medium text-blue-800" data-id="lnp9lc2v8" data-path="src/components/grades/ExcelGradeManager.tsx">Total Students</p>
                  <p className="text-2xl font-bold text-blue-900" data-id="sdzr9rytp" data-path="src/components/grades/ExcelGradeManager.tsx">{analytics.totalStudents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200" data-id="ms15gbbcq" data-path="src/components/grades/ExcelGradeManager.tsx">
            <CardContent className="p-4" data-id="ddsjhre72" data-path="src/components/grades/ExcelGradeManager.tsx">
              <div className="flex items-center gap-3" data-id="ktmsfyk8m" data-path="src/components/grades/ExcelGradeManager.tsx">
                <FileSpreadsheet className="h-8 w-8 text-green-600" data-id="rdaynkzti" data-path="src/components/grades/ExcelGradeManager.tsx" />
                <div data-id="dlri56uo2" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <p className="text-sm font-medium text-green-800" data-id="31lg552xh" data-path="src/components/grades/ExcelGradeManager.tsx">Assignments</p>
                  <p className="text-2xl font-bold text-green-900" data-id="dtpxclh97" data-path="src/components/grades/ExcelGradeManager.tsx">{analytics.totalAssignments}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200" data-id="2k7xhaq30" data-path="src/components/grades/ExcelGradeManager.tsx">
            <CardContent className="p-4" data-id="mg002b2yy" data-path="src/components/grades/ExcelGradeManager.tsx">
              <div className="flex items-center gap-3" data-id="qijx4wec2" data-path="src/components/grades/ExcelGradeManager.tsx">
                <TrendingUp className="h-8 w-8 text-purple-600" data-id="fmr1nd7ff" data-path="src/components/grades/ExcelGradeManager.tsx" />
                <div data-id="u79waz2im" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <p className="text-sm font-medium text-purple-800" data-id="18pl3d0w6" data-path="src/components/grades/ExcelGradeManager.tsx">Average Grade</p>
                  <p className="text-2xl font-bold text-purple-900" data-id="gtt92djrl" data-path="src/components/grades/ExcelGradeManager.tsx">{analytics.averageGrade.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200" data-id="gz7gfampy" data-path="src/components/grades/ExcelGradeManager.tsx">
            <CardContent className="p-4" data-id="3wfpw9puw" data-path="src/components/grades/ExcelGradeManager.tsx">
              <div className="flex items-center gap-3" data-id="0pccbnzgr" data-path="src/components/grades/ExcelGradeManager.tsx">
                <div className="relative" data-id="sieqap0h8" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <div className="h-8 w-8 bg-orange-200 rounded-full flex items-center justify-center" data-id="ov9dbropp" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <span className="text-sm font-bold text-orange-700" data-id="d2nhe98vz" data-path="src/components/grades/ExcelGradeManager.tsx">{analytics.passRate.toFixed(0)}%</span>
                  </div>
                </div>
                <div data-id="hkynxlia3" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <p className="text-sm font-medium text-orange-800" data-id="m5lfexn52" data-path="src/components/grades/ExcelGradeManager.tsx">Pass Rate</p>
                  <Progress value={analytics.passRate} className="w-16 h-2 mt-1" data-id="7syv2gf9h" data-path="src/components/grades/ExcelGradeManager.tsx" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200" data-id="3pjcbzu0x" data-path="src/components/grades/ExcelGradeManager.tsx">
            <CardContent className="p-4" data-id="fcmbfhteh" data-path="src/components/grades/ExcelGradeManager.tsx">
              <div className="flex items-center gap-3" data-id="u9a55qjze" data-path="src/components/grades/ExcelGradeManager.tsx">
                <Calculator className="h-8 w-8 text-indigo-600" data-id="pqosksbq8" data-path="src/components/grades/ExcelGradeManager.tsx" />
                <div data-id="x6acfl5fg" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <p className="text-sm font-medium text-indigo-800" data-id="ojiznzy4m" data-path="src/components/grades/ExcelGradeManager.tsx">Formula Cells</p>
                  <p className="text-2xl font-bold text-indigo-900" data-id="y8o4dcvff" data-path="src/components/grades/ExcelGradeManager.tsx">{analytics.formulaCells}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="05gq265ap" data-path="src/components/grades/ExcelGradeManager.tsx">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100" data-id="447ofn1p7" data-path="src/components/grades/ExcelGradeManager.tsx">
          <TabsTrigger value="overview" className="flex items-center gap-2" data-id="6rlzq6xy1" data-path="src/components/grades/ExcelGradeManager.tsx">
            <Eye className="h-4 w-4" data-id="7pwa8bqzn" data-path="src/components/grades/ExcelGradeManager.tsx" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="sheets" className="flex items-center gap-2" data-id="wa459vpl2" data-path="src/components/grades/ExcelGradeManager.tsx">
            <FileSpreadsheet className="h-4 w-4" data-id="3xyrodzry" data-path="src/components/grades/ExcelGradeManager.tsx" />
            Grade Sheets ({gradeSheets.length})
          </TabsTrigger>
          {selectedSheet &&
          <TabsTrigger value="editor" className="flex items-center gap-2" data-id="53gkcbdzw" data-path="src/components/grades/ExcelGradeManager.tsx">
              <Edit className="h-4 w-4" data-id="oxfgwm0fn" data-path="src/components/grades/ExcelGradeManager.tsx" />
              Editor - {selectedSheet.sheet_name}
              {hasUnsavedChanges && <span className="ml-1 text-orange-500" data-id="ikimsz0go" data-path="src/components/grades/ExcelGradeManager.tsx">*</span>}
            </TabsTrigger>
          }
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6" data-id="ceaq92jhi" data-path="src/components/grades/ExcelGradeManager.tsx">
          <div className="grid md:grid-cols-2 gap-6" data-id="5z3gek2nf" data-path="src/components/grades/ExcelGradeManager.tsx">
            <Card data-id="oa707j87q" data-path="src/components/grades/ExcelGradeManager.tsx">
              <CardHeader data-id="sw01npi81" data-path="src/components/grades/ExcelGradeManager.tsx">
                <CardTitle data-id="bkb9rzdfb" data-path="src/components/grades/ExcelGradeManager.tsx">Getting Started</CardTitle>
                <CardDescription data-id="uy2ix503i" data-path="src/components/grades/ExcelGradeManager.tsx">Follow these steps to manage your grades professionally</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4" data-id="wk3d6g3fr" data-path="src/components/grades/ExcelGradeManager.tsx">
                <div className="flex items-start gap-3" data-id="j74pc0ybr" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold" data-id="7dbl3lg7m" data-path="src/components/grades/ExcelGradeManager.tsx">1</div>
                  <div data-id="0tifolnyu" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <p className="font-medium" data-id="7tzie4eez" data-path="src/components/grades/ExcelGradeManager.tsx">Download Template</p>
                    <p className="text-sm text-gray-600" data-id="bvp9gorzp" data-path="src/components/grades/ExcelGradeManager.tsx">Start with our professional grade sheet template</p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="pos9jun7w" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold" data-id="yrdd6k051" data-path="src/components/grades/ExcelGradeManager.tsx">2</div>
                  <div data-id="m33hdylah" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <p className="font-medium" data-id="0n3vv3pz3" data-path="src/components/grades/ExcelGradeManager.tsx">Upload Your Data</p>
                    <p className="text-sm text-gray-600" data-id="45vgdlp24" data-path="src/components/grades/ExcelGradeManager.tsx">Upload Excel files with student grades and formulas</p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="x0himv1sd" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold" data-id="5gvq5fgo7" data-path="src/components/grades/ExcelGradeManager.tsx">3</div>
                  <div data-id="jcmeq6m5j" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <p className="font-medium" data-id="w8tg9bcfh" data-path="src/components/grades/ExcelGradeManager.tsx">Edit & Analyze</p>
                    <p className="text-sm text-gray-600" data-id="q75seuqay" data-path="src/components/grades/ExcelGradeManager.tsx">Use our advanced editor with sorting, filtering, and analytics</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-id="jrxw8apvr" data-path="src/components/grades/ExcelGradeManager.tsx">
              <CardHeader data-id="6g1d74a13" data-path="src/components/grades/ExcelGradeManager.tsx">
                <CardTitle data-id="vgauez4xw" data-path="src/components/grades/ExcelGradeManager.tsx">Recent Activity</CardTitle>
                <CardDescription data-id="qrfr565i6" data-path="src/components/grades/ExcelGradeManager.tsx">Your latest grade sheet activities</CardDescription>
              </CardHeader>
              <CardContent data-id="67habt7bn" data-path="src/components/grades/ExcelGradeManager.tsx">
                {gradeSheets.length > 0 ?
                <div className="space-y-3" data-id="v98luq24a" data-path="src/components/grades/ExcelGradeManager.tsx">
                    {gradeSheets.slice(0, 3).map((sheet) =>
                  <div key={sheet.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="l1xgyv9tg" data-path="src/components/grades/ExcelGradeManager.tsx">
                        <div data-id="e7j4yg87v" data-path="src/components/grades/ExcelGradeManager.tsx">
                          <p className="font-medium" data-id="ez6hge7oo" data-path="src/components/grades/ExcelGradeManager.tsx">{sheet.sheet_name}</p>
                          <p className="text-sm text-gray-600" data-id="utxyegl43" data-path="src/components/grades/ExcelGradeManager.tsx">
                            Modified {new Date(sheet.last_modified_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSheetSelect(sheet)} data-id="kwdv7mi4i" data-path="src/components/grades/ExcelGradeManager.tsx">

                          <Edit className="h-3 w-3 mr-1" data-id="o7q302z4g" data-path="src/components/grades/ExcelGradeManager.tsx" />
                          Edit
                        </Button>
                      </div>
                  )}
                  </div> :

                <div className="text-center py-8" data-id="hbcstut1n" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400 mb-4" data-id="psl9tmprn" data-path="src/components/grades/ExcelGradeManager.tsx" />
                    <p className="text-gray-600" data-id="iqg9itg6x" data-path="src/components/grades/ExcelGradeManager.tsx">No grade sheets yet</p>
                    <Button
                    className="mt-3"
                    onClick={() => setShowUploadDialog(true)} data-id="20wp7w4ni" data-path="src/components/grades/ExcelGradeManager.tsx">

                      <Plus className="h-4 w-4 mr-2" data-id="4t783rqcm" data-path="src/components/grades/ExcelGradeManager.tsx" />
                      Upload First Sheet
                    </Button>
                  </div>
                }
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sheets Tab */}
        <TabsContent value="sheets" className="space-y-4" data-id="65clcxwe2" data-path="src/components/grades/ExcelGradeManager.tsx">
          {gradeSheets.length === 0 ?
          <Card data-id="dp115m59m" data-path="src/components/grades/ExcelGradeManager.tsx">
              <CardContent className="text-center py-12" data-id="465fjqdnj" data-path="src/components/grades/ExcelGradeManager.tsx">
                <FileSpreadsheet className="mx-auto h-16 w-16 text-gray-400 mb-4" data-id="wn930ksqc" data-path="src/components/grades/ExcelGradeManager.tsx" />
                <h3 className="text-xl font-medium mb-2" data-id="ohrfrntuq" data-path="src/components/grades/ExcelGradeManager.tsx">No Grade Sheets</h3>
                <p className="text-gray-600 mb-6" data-id="n362x465e" data-path="src/components/grades/ExcelGradeManager.tsx">
                  Upload your first Excel grade sheet to get started with professional grade management
                </p>
                <Button onClick={() => setShowUploadDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-id="fd6e9xyzx" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <Plus className="h-4 w-4 mr-2" data-id="w32c85qnz" data-path="src/components/grades/ExcelGradeManager.tsx" />
                  Upload Grade Sheet
                </Button>
              </CardContent>
            </Card> :

          <div className="grid gap-4" data-id="78evzz4hr" data-path="src/components/grades/ExcelGradeManager.tsx">
              {gradeSheets.map((sheet) =>
            <Card key={sheet.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500" data-id="4f177x2e8" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <CardContent className="p-6" data-id="8oqnyew4w" data-path="src/components/grades/ExcelGradeManager.tsx">
                    <div className="flex items-center justify-between" data-id="m2v2mgecw" data-path="src/components/grades/ExcelGradeManager.tsx">
                      <div className="flex-1 cursor-pointer" onClick={() => handleSheetSelect(sheet)} data-id="9nv2nra49" data-path="src/components/grades/ExcelGradeManager.tsx">
                        <div className="flex items-center gap-3 mb-3" data-id="k3bjrlrcv" data-path="src/components/grades/ExcelGradeManager.tsx">
                          <div className="p-2 bg-blue-100 rounded-lg" data-id="5qyx3zw3y" data-path="src/components/grades/ExcelGradeManager.tsx">
                            <FileSpreadsheet className="h-5 w-5 text-blue-600" data-id="motv6sone" data-path="src/components/grades/ExcelGradeManager.tsx" />
                          </div>
                          <div data-id="yeuictpxi" data-path="src/components/grades/ExcelGradeManager.tsx">
                            <h3 className="text-lg font-semibold" data-id="sc7h6snbb" data-path="src/components/grades/ExcelGradeManager.tsx">{sheet.sheet_name}</h3>
                            <p className="text-sm text-gray-600" data-id="sy1k67m8n" data-path="src/components/grades/ExcelGradeManager.tsx">
                              File: {sheet.original_filename}
                            </p>
                          </div>
                          <div className="flex gap-2" data-id="y4yd6uvxy" data-path="src/components/grades/ExcelGradeManager.tsx">
                            {sheet.has_unsaved_changes &&
                        <Badge variant="outline" className="text-orange-600 border-orange-300" data-id="026bfgpm0" data-path="src/components/grades/ExcelGradeManager.tsx">
                                Unsaved Changes
                              </Badge>
                        }
                            {selectedSheet?.id === sheet.id &&
                        <Badge className="bg-blue-100 text-blue-700" data-id="9z8210yah" data-path="src/components/grades/ExcelGradeManager.tsx">Current</Badge>
                        }
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm" data-id="274jx2jpt" data-path="src/components/grades/ExcelGradeManager.tsx">
                          <div data-id="2ssolwgsn" data-path="src/components/grades/ExcelGradeManager.tsx">
                            <span className="text-gray-500" data-id="6uuxvjop8" data-path="src/components/grades/ExcelGradeManager.tsx">Students:</span>
                            <span className="ml-1 font-medium" data-id="aacnmwlge" data-path="src/components/grades/ExcelGradeManager.tsx">{sheet.sheet_data.length - 1 || 0}</span>
                          </div>
                          <div data-id="h7l3o3lse" data-path="src/components/grades/ExcelGradeManager.tsx">
                            <span className="text-gray-500" data-id="d773hu8vz" data-path="src/components/grades/ExcelGradeManager.tsx">Columns:</span>
                            <span className="ml-1 font-medium" data-id="jzpeqw0eu" data-path="src/components/grades/ExcelGradeManager.tsx">{sheet.sheet_data[0]?.length || 0}</span>
                          </div>
                          <div data-id="zaxkznwtf" data-path="src/components/grades/ExcelGradeManager.tsx">
                            <span className="text-gray-500" data-id="vtfifdjww" data-path="src/components/grades/ExcelGradeManager.tsx">Modified:</span>
                            <span className="ml-1 font-medium" data-id="dm0ervbht" data-path="src/components/grades/ExcelGradeManager.tsx">{new Date(sheet.last_modified_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2" data-id="u6wd3xhot" data-path="src/components/grades/ExcelGradeManager.tsx">
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSheetSelect(sheet)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50" data-id="db3goutrv" data-path="src/components/grades/ExcelGradeManager.tsx">

                          <Edit className="h-4 w-4 mr-1" data-id="ecpww1exc" data-path="src/components/grades/ExcelGradeManager.tsx" />
                          Edit
                        </Button>
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteSheet(sheet.id)}
                      className="text-red-600 hover:text-red-700 border-red-300 hover:bg-red-50" data-id="pde9g3jw9" data-path="src/components/grades/ExcelGradeManager.tsx">

                          <Trash2 className="h-4 w-4" data-id="xdhypq2ij" data-path="src/components/grades/ExcelGradeManager.tsx" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
            )}
            </div>
          }
        </TabsContent>

        {/* Editor Tab */}
        <TabsContent value="editor" data-id="acri3c1py" data-path="src/components/grades/ExcelGradeManager.tsx">
          {selectedSheet ?
          <div className="space-y-4" data-id="7r63dv5dt" data-path="src/components/grades/ExcelGradeManager.tsx">
              {hasUnsavedChanges &&
            <Alert data-id="34ngqxo2q" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <AlertTriangle className="h-4 w-4" data-id="ek4pzqp5a" data-path="src/components/grades/ExcelGradeManager.tsx" />
                  <AlertDescription data-id="uyq3k74t8" data-path="src/components/grades/ExcelGradeManager.tsx">
                    You have unsaved changes in <strong data-id="v599ur8yb" data-path="src/components/grades/ExcelGradeManager.tsx">{selectedSheet.sheet_name}</strong>. 
                    Don't forget to save your work!
                  </AlertDescription>
                </Alert>
            }
              
              <SpreadsheetViewer
              data={currentSheetData}
              columnDefinitions={selectedSheet.column_definitions}
              onDataChange={handleDataChange}
              onSave={handleSave}
              onExport={handleExport}
              onRevert={handleRevert}
              hasUnsavedChanges={hasUnsavedChanges}
              isLoading={isLoading} data-id="r67kttnb8" data-path="src/components/grades/ExcelGradeManager.tsx" />

            </div> :

          <Card data-id="xpwjzlb4q" data-path="src/components/grades/ExcelGradeManager.tsx">
              <CardContent className="text-center py-12" data-id="gkwrzpv9r" data-path="src/components/grades/ExcelGradeManager.tsx">
                <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400 mb-4" data-id="isz7ze8nr" data-path="src/components/grades/ExcelGradeManager.tsx" />
                <p className="text-gray-600 mb-4" data-id="deburri7q" data-path="src/components/grades/ExcelGradeManager.tsx">Select a grade sheet from the Sheets tab to start editing</p>
                <Button onClick={() => setActiveTab('sheets')} variant="outline" data-id="85rxlgw52" data-path="src/components/grades/ExcelGradeManager.tsx">
                  <FileSpreadsheet className="h-4 w-4 mr-2" data-id="d9qxzg45l" data-path="src/components/grades/ExcelGradeManager.tsx" />
                  Browse Grade Sheets
                </Button>
              </CardContent>
            </Card>
          }
        </TabsContent>
      </Tabs>
    </div>);

};

export default ExcelGradeManager;